import { Injectable } from '@angular/core';
import { Observable, catchError, map, of, tap, throwError } from 'rxjs';
import { v4 as uuidv4 } from 'uuid';

import {
	ApolloQueryError,
	ApolloQueryErrorMapper,
	CombiningSubscriptionManager,
	NodeChangeType,
	SubscriptionManagerService
} from '@shure/shared/angular/data-access/system-api/core';
import { Response, ResponseState } from '@shure/shared/angular/data-access/system-api/models';
import { ILogger } from '@shure/shared/angular/utils/logging';
import { noMoreSubscribers } from '@shure/shared/typescript/rxjs';

import { CompressorApiService, CompressorChannel } from '../../api';
import {
	CompressorChannelMutationGQL,
	CompressorChannelQueryGQL,
	CompressorChannelSubscriptionGQL
} from '../graphql/generated/compressor.generated';
import { mapCompressorChannelFromSysApi } from '../mappers';

@Injectable()
export class DaiDeviceUiDspCompressorService implements CompressorApiService {
	private readonly logger: ILogger;

	private readonly compressorChannelSubscriptionManager: CombiningSubscriptionManager;

	constructor(
		logger: ILogger,
		private readonly compressorChannelQueryGQL: CompressorChannelQueryGQL,
		private readonly compressorChannelMutationGQL: CompressorChannelMutationGQL,
		compressorChannelSubscriptionGQL: CompressorChannelSubscriptionGQL,
		subscriptionManagerService: SubscriptionManagerService
	) {
		this.logger = logger.createScopedLogger('DaiDeviceUiDspCompressorService');

		this.compressorChannelSubscriptionManager = new CombiningSubscriptionManager(
			{
				name: 'DaiDeviceUiDspCompressorService',
				changeTypes: [NodeChangeType.AudioChannelCompressor, NodeChangeType.AudioChannelChannelConfiguration],
				subscriptionGql: compressorChannelSubscriptionGQL
			},
			logger,
			subscriptionManagerService
		);
	}

	public getChannel$(channelId: string): Observable<CompressorChannel> {
		const queryId = uuidv4();
		this.logger.trace('getChannel$()', 'Querying compressor channel', { channelId, queryId });

		return this.compressorChannelQueryGQL.watch({ channelId }, { fetchPolicy: 'network-only' }).valueChanges.pipe(
			map((query) => {
				if (query.data?.node && 'features' in query.data.node) {
					this.logger.trace('getChannel$()', 'Query updated', { channelId });
					return mapCompressorChannelFromSysApi(query.data.node);
				}

				this.logger.error('getChannel$()', 'Failed to query channel', { channelId });
				throw ApolloQueryErrorMapper.getError(query);
			}),
			tap((channel) => this.compressorChannelSubscriptionManager.subscribe(queryId, channel.id)),
			noMoreSubscribers(() => {
				this.logger.trace('getChannel$', 'noMoreSubscribers', { channelId, queryId });
				this.compressorChannelSubscriptionManager.unsubscribeQuery(queryId);
			}),
			catchError((error) => {
				return throwError(() => new ApolloQueryError(error));
			})
		);
	}

	public toggleCompressor(channelId: string, enabled: boolean): Observable<Response<void, string>> {
		this.logger.trace('toggleCompressor()', 'Toggling compressor', { channelId, enabled });
		return this.updateChannel(channelId, { enabled });
	}

	public setThreshold(channelId: string, threshold: number): Observable<Response<void, string>> {
		this.logger.trace('setThreshold()', 'Setting threshold', { channelId, threshold });
		return this.updateChannel(channelId, { threshold });
	}

	public setRatio(channelId: string, ratio: number): Observable<Response<void, string>> {
		this.logger.trace('toggleAgc()', 'Setting ratio', { channelId, ratio });
		return this.updateChannel(channelId, { ratio });
	}

	private updateChannel(
		channelId: string,
		update: Partial<CompressorChannel['features']['compressor']>
	): Observable<Response<void, string>> {
		return this.compressorChannelMutationGQL
			.mutate(
				{
					updates: {
						audioChannel: {
							id: channelId,
							features: {
								compressor: {
									...update
								}
							}
						}
					},
					injectKeyring: '//supplied-by-keyring-injector'
				},
				{ errorPolicy: 'all' }
			)
			.pipe(
				map((mutation) => {
					if (
						mutation.data?.updateNodes &&
						'error' in mutation.data.updateNodes[0] &&
						mutation.data.updateNodes[0].error !== null
					) {
						this.logger.error('updateChannel()', 'Failed to update channel', {
							channelId,
							mutation
						});
						return { state: ResponseState.Error, error: mutation.data.updateNodes[0].error?.message };
					}

					this.logger.trace('updateChannel()', 'Done', { channelId });
					return { state: ResponseState.Done };
				}),
				catchError((error) => {
					this.logger.error('updateChannel()', 'Failed to update channel', { error });
					return of({ state: ResponseState.Error });
				})
			);
	}
}
